import { useAuth0 } from "@auth0/auth0-react";
import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loading } from "../components/Loading";
import {
  AddCvModal,
  BrowseResumeBankModal,
} from "../components/dashboard";
import { CreateJobModal } from "../components/dashboard/create-job-modal";
import { JobOffer } from "../models/job";
import {
  useAllJobOffers,
  useArchiveJobOffer,
  useDeleteJobOffer,
  jobKeys,
} from "../hooks/useJobOffers";
import { useInvalidateUsageQueries } from "../hooks/useUsage";
import { useGuardedAction } from "../hooks/useGuardedAction";
import { JobStatus } from "../types/job.types";
import { hasMissingJobDescriptionOrCompanyInfo } from "../utils/missingFieldsDetection";
import { useDemoMode } from "../components/providers/DemoModeContext";

import { ConfirmationDialog } from "../components/ui/ConfirmationDialog";
import { ManatalBadge } from "../components/dashboard/ManatalBadge";
import { AlertCircle } from "lucide-react";
import {
  FileText,
  Archive,
  Trash2,
  Plus,
  Users,
  FilePlus2,
  Eye,
  ClipboardList,
  FileEdit,
  CheckCircle2,
  Search,
  Calendar,
  Loader2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from '@/components/ui/atoms/Button';
import { PaginationBar, SearchInput, Filter } from '../components/ui';
import { FilterOption } from '../components/ui/filter';
import { createGTMTracking, getGTMAttributes } from '../utils/gtm/gtm-tracking';
import { useAuthRoles } from "../hooks/useAuthRoles";
import { formatDate } from "@/utils/date";

// Pagination config
const DEFAULT_PAGE_SIZE = 20;

// Filter type that includes archived
type JobFilter = JobStatus | 'all' | 'archived';

export const Dashboard = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { isLoading: isAuthLoading } = useAuth0();
  const { isAdmin } = useAuthRoles();

  const JOB_FILTERS: FilterOption<JobFilter>[] = [
    { value: 'all', label: t('dashboard:filters.all'), icon: ClipboardList },
    { value: JobStatus.DRAFT, label: t('dashboard:filters.draft'), icon: FileEdit },
    { value: JobStatus.ANALYZING, label: t('dashboard:filters.analyzing'), icon: Search },
    { value: JobStatus.ACTIVE, label: t('dashboard:filters.active'), icon: CheckCircle2 },
    { value: JobStatus.INTERVIEWING, label: t('dashboard:filters.interviewing'), icon: Users },
    { value: 'archived', label: t('dashboard:filters.archived'), icon: Archive },
  ];
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCvModal, setShowCvModal] = useState(false);
  const [showStepperModal, setShowStepperModal] = useState(false);
  const [stepperModalJobId, setStepperModalJobId] = useState<string | undefined>(undefined);
  const [stepperInitialPhase, setStepperInitialPhase] = useState<'input' | 'review'>('input');
  const [showBrowseResumeBankModal, setShowBrowseResumeBankModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'delete' | 'archive' | 'restore' | null>(null);
  const [selectedJobForAction, setSelectedJobForAction] = useState<JobOffer | null>(null);
  const [dialogOrigin, setDialogOrigin] = useState<string | undefined>(undefined);
  const [selectedJob, setSelectedJob] = useState<JobOffer | null>(null);
  const [showMissingJobInfoModal, setShowMissingJobInfoModal] = useState(false);
  const [jobForMissingInfoModal, setJobForMissingInfoModal] = useState<JobOffer | null>(null);
  const [stepperDrafts, setStepperDrafts] = useState<Record<string, { file: File | null; description: string }>>({});
  const getDismissedStorageKey = useCallback((id: string) => `job-refine-dismissed-${id}`, []);
  const isPersistedProcessingLock = useCallback((id: string): boolean => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.sessionStorage.getItem(getDismissedStorageKey(id)) === '1';
  }, [getDismissedStorageKey]);

  const queryClient = useQueryClient();
  const invalidateUsageQueries = useInvalidateUsageQueries();
  const { isDemoMode, mockJob } = useDemoMode();
  const { guardedAction: guardCvAction } = useGuardedAction('cv', 1, 'dashboard_add_documents');
  const [selectedFilter, setSelectedFilter] = useState<JobFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Pagination state - separate for active and archived to preserve position
  const [activePage, setActivePage] = useState(1);
  const [archivedPage, setArchivedPage] = useState(1);

  // Determine if we're viewing archived jobs
  const isViewingArchived = selectedFilter === 'archived';

  // Get status filter for API - 'all' means no status filter, 'archived' is handled by isActive
  const statusFilter = (selectedFilter !== 'all' && selectedFilter !== 'archived')
    ? selectedFilter as JobStatus
    : undefined;

  // Fetch active jobs with optional status filter
  const { data: activeJobsData, isLoading: isActiveJobsLoading } = useAllJobOffers(
    true, // is_active = true
    activePage,
    DEFAULT_PAGE_SIZE,
    statusFilter, // Pass status filter to API
    debouncedSearchTerm,
    { refetchInterval: 5000 },
    isAdmin ? 'admin' : 'user'
  );

  // Fetch archived jobs (no status filter needed for archived)
  const { data: archivedJobsData } = useAllJobOffers(
    false, // is_active = false (archived)
    archivedPage,
    DEFAULT_PAGE_SIZE,
    undefined, // No status filter for archived
    debouncedSearchTerm,
    { refetchInterval: false },
    isAdmin ? 'admin' : 'user'
  );

  // Mutations for job actions
  const archiveJobMutation = useArchiveJobOffer();
  const deleteJobMutation = useDeleteJobOffer();

  // Only show loading on initial load, not when switching views
  const isLoading = !isDemoMode && (isAuthLoading ||
    (isActiveJobsLoading && !activeJobsData));

  // Select the right data based on current view
  const jobsData = isViewingArchived ? archivedJobsData : activeJobsData;
  const currentPage = isViewingArchived ? archivedPage : activePage;
  const setCurrentPage = isViewingArchived ? setArchivedPage : setActivePage;

  // Extract job offers from the response data
  const demoJobs = isDemoMode && !isViewingArchived && mockJob ? [mockJob] : [];
  const jobs = demoJobs.length > 0 ? demoJobs : jobsData?.items || [];

  // Pagination info
  const totalPages = demoJobs.length > 0 ? 1 : jobsData?.pages || 1;
  const totalJobs = demoJobs.length > 0 ? demoJobs.length : jobsData?.total || 0;

  // Handle filter changes
  const handleFilterChange = useCallback((filter: JobFilter) => {
    setSelectedFilter(filter);
    if (filter !== 'archived' && selectedFilter !== 'archived') {
      setActivePage(1);
    }
  }, [selectedFilter]);

  // Reset page when search changes
  const handleDebouncedSearchChange = useCallback((value: string) => {
    setDebouncedSearchTerm(value);
    isViewingArchived ? setArchivedPage(1) : setActivePage(1);
  }, [isViewingArchived]);

  const filteredJobs = jobs;

  // Clear stale processing locks as soon as backend signals completion.
  useEffect(() => {
    const knownJobs = [
      ...(activeJobsData?.items || []),
      ...(archivedJobsData?.items || []),
    ];

    // Always detect completion from persisted background-processing marker.
    knownJobs.forEach((job) => {
      if (typeof window === 'undefined' || job.requiresReview) {
        return;
      }
      const dismissedKey = getDismissedStorageKey(job.id);
      const hadBackgroundProcessingFlag = window.sessionStorage.getItem(dismissedKey) === '1';
      if (hadBackgroundProcessingFlag) {
        toast.success(t('dashboard:toasts.updated_success'));
        window.sessionStorage.removeItem(dismissedKey);
      }
    });
  }, [activeJobsData?.items, archivedJobsData?.items, getDismissedStorageKey]);

  // Auto-open the create-job stepper when navigated with ?new=1 (e.g. from ⌘K palette)
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setStepperModalJobId(undefined);
      setShowStepperModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (isLoading) {
    return <Loading />;
  }

  const handleArchiveJob = (job: JobOffer) => {
    const loadingToastId = toast.loading(t('dashboard:toasts.archiving', { title: job.title }));

    archiveJobMutation.mutate(job.id, {
      onSuccess: () => {
        toast.dismiss(loadingToastId);
        toast.success(t('dashboard:toasts.archived'));
      },
      onError: (error) => {
        toast.dismiss(loadingToastId);
        toast.error(t('dashboard:toasts.archive_failed', { error: error.message }));
      }
    });
  };

  const handleViewDetails = (job: JobOffer) => {
    const isExamConfigProcessing =
      isPersistedProcessingLock(job.id) &&
      Boolean(job.requiresReview) &&
      (job.status === JobStatus.ACTIVE || job.status === JobStatus.INTERVIEWING);

    // Check if job is DRAFT with missing job description/company info
    if (job.status === JobStatus.DRAFT && hasMissingJobDescriptionOrCompanyInfo(job)) {
      setJobForMissingInfoModal(job);
      setShowMissingJobInfoModal(true);
      return;
    }

    if (job.status === JobStatus.ANALYZING) {
      toast.info(t('dashboard:toasts.analyzing_wait'));
      return;
    }

    if (isExamConfigProcessing) {
      toast.info(t('dashboard:toasts.exam_processing_wait'));
      return;
    }

    // Jobs that require review reopen directly in the Create Job stepper.
    if (job.requiresReview && (job.status === JobStatus.ACTIVE || job.status === JobStatus.INTERVIEWING)) {
      openJobDescriptionStepper(job, 'review');
      return;
    }

    // Small refresh when opening a job: clear stale detail/candidate caches first.
    queryClient.removeQueries({ queryKey: jobKeys.detail(job.id) });
    queryClient.removeQueries({ queryKey: jobKeys.candidatesForJob(job.id) });
    navigate(`/job/${job.id}`);
  };

  const openJobDescriptionStepper = (job: JobOffer, initialPhase: 'input' | 'review' = 'input') => {
    const draftKey = job.id;
    if (!stepperDrafts[draftKey]) {
      setStepperDrafts((previous) => ({
        ...previous,
        [draftKey]: {
          file: null,
          description: job.customInstructions ?? job.description ?? '',
        },
      }));
    }
    // CreateJobModal skips title input when given an existing jobId.
    setStepperModalJobId(job.id);
    setStepperInitialPhase(initialPhase);
    setShowStepperModal(true);
  };

  const handleUploadJobDescription = () => {
    if (jobForMissingInfoModal) {
      openJobDescriptionStepper(jobForMissingInfoModal);
      setShowMissingJobInfoModal(false);
      setJobForMissingInfoModal(null);
    }
  };

  const handleCloseMissingJobInfoModal = () => {
    setShowMissingJobInfoModal(false);
    setJobForMissingInfoModal(null);
  };

  const handleAddDocuments = (job: JobOffer) => {
    setSelectedJob(job);
    if (job.status === JobStatus.DRAFT) {
      openJobDescriptionStepper(job);
    } else if (job.status === JobStatus.ANALYZING) {
      toast.info(t('dashboard:toasts.upload_wait'));
    } else {
      guardCvAction(() => {
        setShowCvModal(true);
      })();
    }
  };

  const closeCvModal = () => {
    setShowCvModal(false);
    setSelectedJob(null);
  };

  const handleCreateFirstJob = () => {
    setStepperDrafts((previous) => ({
      ...previous,
      __new__: { file: null, description: '' },
    }));
    setStepperModalJobId(undefined);
    setStepperInitialPhase('input');
    setShowStepperModal(true);
  };

  const closeStepperModal = () => {
    setShowStepperModal(false);
    setStepperModalJobId(undefined);
  };

  // Unified dialog handlers
  const handleOpenDialog = (type: 'delete' | 'archive' | 'restore', job: JobOffer, origin?: string) => {
    setDialogType(type);
    setSelectedJobForAction(job);
    setDialogOrigin(origin);
    setShowConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setShowConfirmDialog(false);
    setDialogType(null);
    setSelectedJobForAction(null);
    setDialogOrigin(undefined);
  };

  const handleConfirmAction = () => {
    if (!selectedJobForAction || !dialogType) return;

    if (dialogType === 'delete') {
      handleDeleteJob(selectedJobForAction);
    } else if (dialogType === 'archive') {
      handleArchiveJob(selectedJobForAction);
    }
    // Note: restore functionality would need a backend endpoint

    handleCloseDialog();
  };

  const handleDeleteJob = (job: JobOffer) => {
    const loadingToastId = toast.loading(t('dashboard:toasts.deleting', { title: job.title }));

    deleteJobMutation.mutate(job.id, {
      onSuccess: () => {
        toast.dismiss(loadingToastId);
        toast.success(t('dashboard:toasts.deleted', { title: job.title }));
      },
      onError: (error) => {
        toast.dismiss(loadingToastId);
        toast.error(t('dashboard:toasts.delete_failed', { error: error.message }));
      }
    });
  };

  const handleJobCreated = () => {
    // Invalidate all job-related queries to ensure fresh data
    queryClient.invalidateQueries({ queryKey: jobKeys.lists() });

    // Invalidate usage queries to reflect updated usage counts
    invalidateUsageQueries();
  };

  const stepperDraftKey = stepperModalJobId ?? '__new__';
  const stepperDraft = stepperDrafts[stepperDraftKey] ?? { file: null, description: '' };

  return (
    <>
      <div className="bg-background-gray h-full overflow-hidden flex flex-col">
        {/* Page header row — title + toolbar pinned at the top of the page,
            aligned with Plans & Billing / Integrations / Settings. */}
        <div className="shrink-0 px-6 lg:px-10 pt-6 md:pt-10 pb-5">
          <div
            data-tour="job-listings"
            className="flex items-center md:items-end justify-between gap-2 md:gap-3 md:flex-wrap"
          >
            <div className="min-w-0 flex-1 md:flex-initial">
              <h4 className="text-xl md:text-2xl font-semibold text-dark-invirtus font-heading leading-tight">
                {isViewingArchived ? t('dashboard:page.title_archived') : t('dashboard:page.title')}
              </h4>
              <p className="hidden md:block mt-1 text-sm text-dark-invirtus/60 font-body">
                {isViewingArchived
                  ? t('dashboard:page.subtitle_archived')
                  : t('dashboard:page.subtitle')}
              </p>
            </div>
            <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={t('dashboard:toolbar.search_placeholder')}
                debounceDelay={500}
                onDebouncedChange={handleDebouncedSearchChange}
                showClearButton={true}
                mobileToggle={true}
                gtmTracking={createGTMTracking('SEARCH_JOBS', { origin: 'dashboard_header' })}
              />
            <div className="flex items-center gap-2 shrink-0">

                {/* Filter — icon button + dropdown */}
                <Filter
                  filters={JOB_FILTERS}
                  selected={selectedFilter}
                  onChange={handleFilterChange}
                  defaultValue="all"
                  ariaLabel={t('dashboard:toolbar.filter_aria')}
                  getFilterItemTracking={(value) =>
                    createGTMTracking('FILTER_JOBS', {
                      origin: 'dashboard_header',
                      target: value,
                    })
                  }
                />

                {/* Divider — separates list controls (search · filter) from
                    the primary page action (Create). */}
                <span
                  aria-hidden
                  className="h-6 w-px bg-light-lila/60 mx-0.5"
                />

                <Button
                  {...getGTMAttributes(
                    createGTMTracking('JOB_OFFER_CREATION_WORKFLOW', {
                      origin: 'dashboard_header',
                      target: 'open_stepper',
                      step: 'start',
                      status: 'in_progress',
                      mode: 'create',
                    }),
                  )}
                  data-tour="create-job"
                  onClick={handleCreateFirstJob}
                  label={t('dashboard:toolbar.create_job')}
                  labelClassName="hidden md:inline"
                  icon={<Plus/>}
                  type="contained"
                  intent="secondary"
                  title={t('dashboard:toolbar.create_job')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Workspace — bordered region that fills the remaining viewport.
            Internal layout: thin meta strip, scrollable list, sticky
            pagination footer. Bottom padding clears the floating chatbot. */}
        <div className="flex-1 min-h-0 px-6 lg:px-10 pb-6 md:pb-10">
          <div className="h-full rounded-2xl border border-light-lila/40 bg-light shadow-[0_1px_2px_0_rgba(16,24,40,0.04)] overflow-hidden flex flex-col">
            {/* Meta strip — internal "label" for the workspace, anchors the
                top edge of the card so the list doesn't float untethered. */}
            <div className="shrink-0 px-5 py-2.5 border-b border-light-lila/30 flex items-center justify-between gap-3 bg-background-gray/40">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-dark-invirtus/55 font-body">
                {isViewingArchived ? t('dashboard:meta.archived_label') : t('dashboard:meta.showing')}
                <span className="ml-1.5 text-dark-invirtus/85 tabular-nums normal-case tracking-normal">
                  {t('dashboard:meta.count_job', { count: totalJobs })}
                </span>
              </span>
              {selectedFilter !== 'all' && !isViewingArchived && (
                <Button
                  type="text"
                  intent="muted"
                  size="xs"
                  label={t('dashboard:filters.clear')}
                  rightIcon={<XCircle/>}
                  onClick={() => handleFilterChange('all')}
                />
              )}
            </div>

            {/* Scrollable list */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4">
              {filteredJobs.length > 0 ? (
                <div className="space-y-2.5">
                  {filteredJobs.map((job) => {
                    const isAnalyzing = job.status === JobStatus.ANALYZING;
                    const isDraft = job.status === JobStatus.DRAFT;
                    const isExamConfigProcessing =
                      isPersistedProcessingLock(job.id) &&
                      Boolean(job.requiresReview) &&
                      (job.status === JobStatus.ACTIVE ||
                        job.status === JobStatus.INTERVIEWING);
                    const isViewBlocked = isAnalyzing || isExamConfigProcessing;
                    const getViewDetailsTarget = (): string => {
                      if (isViewBlocked) return 'blocked';
                      if (isDraft && hasMissingJobDescriptionOrCompanyInfo(job)) {
                        return 'open_missing_info_modal';
                      }
                      return 'open_job_details';
                    };

                    return (
                      <div
                        key={job.id}
                        data-tour="job-offer-card"
                        {...getGTMAttributes(
                          createGTMTracking('JOB_VIEW_DETAILS', {
                            origin: 'job_card',
                            target: getViewDetailsTarget(),
                          }),
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isViewBlocked) return;
                          handleViewDetails(job);
                        }}
                        className={`group p-4 rounded-lg border bg-light transition-all ${
                          isViewBlocked
                            ? 'cursor-not-allowed opacity-70 border-light-lila/40'
                            : 'cursor-pointer border-light-lila/40 hover:border-light-lila hover:shadow-[0_1px_2px_0_rgba(16,24,40,0.06)]'
                        } ${isViewingArchived ? 'opacity-75' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 sm:gap-2.5 mb-1">
                              <div className="flex items-center gap-2 min-w-0">
                                <h6 className="text-base font-bold text-dark-invirtus font-heading truncate">
                                  {job.title}
                                </h6>
                                {job.source === 'MANATAL' && <ManatalBadge size="sm" />}
                              </div>
                            </div>
                            <div className="text-xs sm:text-sm font-body text-dark-invirtus/70 mb-2">
                              {isAnalyzing ? (
                                <span className="flex items-center gap-2 font-medium">
                                  <Loader2 className="animate-spin w-3.5 h-3.5 shrink-0" />
                                  {t('dashboard:card.loading_content')}
                                </span>
                              ) : isExamConfigProcessing ? (
                                <span className="flex items-center gap-2 font-medium">
                                  <Loader2 className="animate-spin w-3.5 h-3.5 shrink-0" />
                                  {t('dashboard:card.updating_info')}
                                </span>
                              ) : isDraft ? (
                                <span className="flex items-center gap-2 font-medium">
                                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                  {t('dashboard:card.add_description')}
                                </span>
                              ) : (
                                <span className="truncate block">
                                  {job.companyName}
                                  {job.location ? ` · ${job.location}` : ' · Remote'}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs font-body text-dark-invirtus/60">
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                <span>
                                  {job.createdAt
                                    ? formatDate(job.createdAt)
                                    : t('common:dates.not_available')}
                                </span>
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                <span>{t('dashboard:card.candidate_count', { count: job.candidateCount ?? 0 })}</span>
                              </span>
                              {isViewingArchived && job.updatedAt && (
                                <span className="inline-flex items-center gap-1">
                                  <Archive className="w-3.5 h-3.5" />
                                  <span>
                                    {t('dashboard:card.archived_on')} {formatDate(job.updatedAt)}
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>

                          <div
                            data-tour="job-card-actions"
                            className="flex items-center gap-0.5 sm:gap-1 shrink-0"
                          >
                            <Button
                              type="icon"
                              intent="muted"
                              size="sm"
                              {...getGTMAttributes(
                                createGTMTracking('JOB_VIEW_DETAILS', {
                                  origin: 'eye_button',
                                  target: isViewBlocked
                                    ? 'blocked'
                                    : isDraft && hasMissingJobDescriptionOrCompanyInfo(job)
                                      ? 'open_missing_info_modal'
                                      : 'open_job_details',
                                }),
                              )}
                              icon={<Eye/>}
                              disabled={isViewBlocked}
                              title={
                                isAnalyzing
                                  ? t('dashboard:card.analyzing_blocked')
                                  : isExamConfigProcessing
                                    ? t('dashboard:card.exam_processing')
                                    : t('dashboard:card.view_job')
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(job);
                              }}
                            />
                            {!isViewingArchived && (
                              <>
                                <Button
                                  {...getGTMAttributes(
                                    job.status === JobStatus.DRAFT
                                      ? createGTMTracking(
                                          'JOB_OFFER_CREATION_WORKFLOW',
                                          {
                                            origin: 'job_card',
                                            target: 'open_stepper',
                                            step: 'job_description',
                                            status: 'in_progress',
                                            mode: 'edit',
                                          },
                                        )
                                      : createGTMTracking('OPEN_UPLOAD_MODAL', {
                                          origin: 'job_card',
                                          target: isAnalyzing ? 'blocked' : 'cvs',
                                        }),
                                  )}
                                  type="icon"
                                  intent="muted"
                                  size="sm"
                                  icon={<FilePlus2/> }
                                  disabled={isAnalyzing}
                                  title={
                                    isAnalyzing
                                      ? t('dashboard:card.analyzing_blocked')
                                      : job.status === JobStatus.DRAFT
                                        ? t('dashboard:card.upload_docs')
                                        : t('dashboard:card.upload_cvs')
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddDocuments(job);
                                  }}
                                />
                                <Button
                                  {...getGTMAttributes(
                                    createGTMTracking('ARCHIVE_JOB', {
                                      origin: 'job_card',
                                      target: 'open_modal',
                                    }),
                                  )}
                                  type="icon"
                                  intent="muted"
                                  size="sm"
                                  icon={<Archive/> }
                                  title={t('dashboard:card.archive_job')}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDialog('archive', job, 'job_card');
                                  }}
                                />
                              </>
                            )}
                            {isViewingArchived && (
                              <Button
                                {...getGTMAttributes(
                                  createGTMTracking('DELETE_JOB', {
                                    origin: 'job_card',
                                    target: 'open_modal',
                                  }),
                                )}
                                type="icon"
                                intent="danger"
                                size="sm"
                                icon={<Trash2/>}
                                title={t('dashboard:card.delete_job')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDialog('delete', job, 'job_card');
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center px-6 py-12 gap-2">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-dark-invirtus-soft mb-1">
                    <FileText className="h-5 w-5 text-dark-invirtus/60" />
                  </div>
                  <h6 className="text-sm font-semibold text-dark-invirtus font-heading">
                    {debouncedSearchTerm.trim()
                      ? t('dashboard:empty.no_match', { search: debouncedSearchTerm })
                      : isViewingArchived
                        ? t('dashboard:empty.no_archived')
                        : selectedFilter === 'all'
                          ? t('dashboard:empty.no_jobs')
                          : t('dashboard:empty.no_status', { status: selectedFilter })}
                  </h6>
                  <p className="text-xs text-dark-invirtus/60 font-body max-w-md">
                    {isViewingArchived
                      ? t('dashboard:empty.archived_hint')
                      : selectedFilter === 'all'
                        ? t('dashboard:empty.first_job_hint')
                        : t('dashboard:empty.filter_hint')}
                  </p>
                  {!isViewingArchived &&
                    selectedFilter === 'all' &&
                    !debouncedSearchTerm.trim() && (
                      <div className="mt-2">
                        <Button
                          type="contained"
                          intent="primary"
                          icon={<Plus/>}
                          label={t('dashboard:empty.create_first')}
                          onClick={handleCreateFirstJob}
                        />
                      </div>
                    )}
                </div>
              )}
            </div>

            {/* Sticky pagination — pinned to the bottom of the workspace so
                it's always visible without scrolling. */}
            {totalPages > 1 && (
              <div className="shrink-0 border-t border-light-lila/30 bg-background-gray/40">
                <PaginationBar
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalJobs}
                  onPageChange={setCurrentPage}
                  itemLabel="jobs"
                  className="border-t-0 bg-transparent"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CV Modal */}
      {showCvModal && selectedJob && (
        <AddCvModal
          job={selectedJob}
          onClose={closeCvModal}
        />
      )}

      {/* Create Job Modal — unified 4-state flow (input → analyzing → review → success) */}
      {showStepperModal && (
        <CreateJobModal
          onClose={closeStepperModal}
          onJobCreated={handleJobCreated}
          jobId={stepperModalJobId}
          initialPhase={stepperInitialPhase}
          initialFile={stepperDraft.file}
          initialDescription={stepperDraft.description}
          onDraftChange={(draft) => {
            setStepperDrafts((previous) => ({
              ...previous,
              [stepperDraftKey]: draft,
            }));
          }}
        />
      )}

      {/* Browse Resume Bank Modal */}
      {showBrowseResumeBankModal && (
        <BrowseResumeBankModal
          onClose={() => setShowBrowseResumeBankModal(false)}
        />
      )}

      {/* No Job Description Modal */}
      {showMissingJobInfoModal && jobForMissingInfoModal && (
        <ConfirmationDialog
          isOpen={showMissingJobInfoModal}
          onClose={handleCloseMissingJobInfoModal}
          title={t('dashboard:dialogs.missing_info_title')}
          icon={<AlertCircle className="text-red-500"/>}
          message={
            jobForMissingInfoModal.title
              ? t('dashboard:dialogs.missing_info_message_named', { title: jobForMissingInfoModal.title })
              : t('dashboard:dialogs.missing_info_message')
          }
          buttons={[
            {
              label: t('common:actions.close'),
              onClick: handleCloseMissingJobInfoModal,
              type: 'outlined',
              intent: 'secondary'
            },
            {
              label: t('dashboard:dialogs.upload_job_description'),
              onClick: handleUploadJobDescription,
              type: 'contained',
              intent: 'secondary',
              ...getGTMAttributes(
                createGTMTracking('JOB_OFFER_CREATION_WORKFLOW', {
                  origin: 'missing_info_modal',
                  target: 'open_stepper',
                  step: 'job_description',
                  status: 'in_progress',
                  mode: 'edit',
                })
              )
            }
          ]}

        />
      )}

      {/* Unified Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={handleCloseDialog}
        title={
          dialogType === 'delete'
            ? t('dashboard:dialogs.confirm_delete_title')
            : dialogType === 'restore'
              ? t('dashboard:dialogs.confirm_restore_title')
              : t('dashboard:dialogs.confirm_archive_title')
        }
        message={
          dialogType === 'delete'
            ? t('dashboard:dialogs.confirm_delete_message')
            : dialogType === 'restore'
              ? t('dashboard:dialogs.confirm_restore_message')
              : t('dashboard:dialogs.confirm_archive_message')
        }
        gtm={dialogType === 'delete' ? 'DELETE_JOB' : 'ARCHIVE_JOB'}
        origin={dialogOrigin}
        buttons={[
          {
            label: t('common:actions.cancel'),
            onClick: handleCloseDialog,
            type: 'outlined',
            intent: 'secondary',
          },
          {
            label:
              dialogType === 'delete'
                ? deleteJobMutation.isPending
                  ? t('common:status.deleting')
                  : t('common:actions.delete')
                : dialogType === 'restore'
                  ? archiveJobMutation.isPending
                    ? t('common:status.restoring')
                    : t('common:actions.restore')
                  : archiveJobMutation.isPending
                    ? t('common:status.archiving')
                    : t('common:actions.archive'),
            onClick: handleConfirmAction,
            type: 'contained',
            intent: dialogType === 'delete' ? 'danger' : 'secondary',
            loading: deleteJobMutation.isPending || archiveJobMutation.isPending,
            disabled: deleteJobMutation.isPending || archiveJobMutation.isPending,
          },
        ]}
      />
    </>
  );
};
